import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { LOOKUP_CODES } from '@core/lookup/lookup-codes';
import { LookupStore } from '@core/lookup/lookup.store';
import { ClientStore } from '@features/client/client.store';
import { EmployeeInterventionsPanelComponent } from '@features/employee/components/employee-interventions-panel/employee-interventions-panel.component';
import { EmployeeResponse } from '@features/employee/employee.model';
import { EmployeeStore } from '@features/employee/employee.store';
import { LookupSelectComponent } from '@shared/components/lookup-select/lookup-select.component';
import { SearchableLookupSelectComponent } from '@shared/components/searchable-lookup-select/searchable-lookup-select.component';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { toNullableDateString } from '@shared/utils/date-utils';
import { cleanRecord } from '@shared/utils/payload-utils';
import { toUpdateRequest } from '../../../intervention-update';
import {
  CreateInterventionRequest,
  InterventionResponse,
  InterventionTypeResponse,
  UpdateInterventionRequest,
} from '../../../intervention.model';

// Who the request is for: a member found on the client's list, or a new member typed in on the phone.
type MemberMode = 'existing' | 'new';

// Step 1 of the wizard, New Request: who the request is for (a member who is found and shown with their history, or a new member), what kind of
// user they are (employee, dependent, manager, ...), and what is being asked for. The rest of the member is completed in the Eligibility step.
// The same form opens on a case that exists (initialValue): the member and the user type stay as they were, and the request is edited.
@Component({
  selector: 'new-request-form',
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    SearchableLookupSelectComponent,
    LookupSelectComponent,
    EmployeeInterventionsPanelComponent,
    ...SharedMaterialUIModule,
  ],
  templateUrl: './new-request-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class NewRequestFormComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly lookupStore = inject(LookupStore);
  private readonly clientStore = inject(ClientStore);
  protected readonly employeeStore = inject(EmployeeStore);

  readonly busy = input(false);
  readonly interventionTypes = input<InterventionTypeResponse[]>([]);
  // The case the step is opened on; left out, the form logs a new request.
  readonly initialValue = input<InterventionResponse | null>(null);
  // A closed case, or a user who cannot update it: the request is shown, not edited.
  readonly readOnly = input(false);
  // A new request, to be created.
  readonly submitted = output<CreateInterventionRequest>();
  // The request of a case that exists, as the whole case to save.
  readonly updated = output<UpdateInterventionRequest>();

  protected readonly isEdit = computed(() => this.initialValue() !== null);

  // Whether the member is looked up or typed in (a signal, so the page changes with it).
  protected readonly mode = signal<MemberMode>('existing');

  // The member typed in for a new member; its controls are switched off while an existing member is used, so they do not count.
  protected readonly newMemberForm = this.formBuilder.group({
    clientCompanyId: ['', Validators.required],
    firstName: ['', Validators.required],
    surname: ['', Validators.required],
    idNumber: [''],
    emailAddress: ['', Validators.email],
    contactNumber: [''],
  });

  protected readonly form = this.formBuilder.group({
    ownerEmployeeId: ['', Validators.required],
    newMember: this.newMemberForm,
    userTypeId: ['', Validators.required],
    interventionTypeId: ['', Validators.required],
    interventionName: [''],
    description: [''],
    reason: [''],
    sourceChannelId: ['', Validators.required],
    requestTypeId: ['', Validators.required],
    priorityId: [''],
    riskClassificationId: [''],
    confidentialityLevelId: [''],
    startDate: [new Date() as Date | string | null, Validators.required],
    targetReviewDate: [null as Date | string | null],
  });

  // The lists of the step, from the reference data.
  protected readonly userTypes = computed(() => this.lookupStore.items(LOOKUP_CODES.memberType));
  protected readonly sourceChannels = computed(() => this.lookupStore.items(LOOKUP_CODES.sourceChannel));
  protected readonly requestTypes = computed(() => this.lookupStore.items(LOOKUP_CODES.requestType));
  protected readonly priorities = computed(() => this.lookupStore.items(LOOKUP_CODES.interventionPriority));
  protected readonly riskRatings = computed(() => this.lookupStore.items(LOOKUP_CODES.riskRating));
  protected readonly confidentialityLevels = computed(() => this.lookupStore.items(LOOKUP_CODES.confidentialityLevel));

  // The customers a new member can belong to, named by client and company.
  protected readonly customers = computed(() =>
    this.clientStore.allClientCompanies().map((item) => ({
      id: item.id,
      name: `${item.clientName} - ${item.companyName}`,
      code: item.uniqueCompanyCode,
    })),
  );

  // The dependant the case is for, when the case is for one (the member card then shows the employee they belong to).
  protected readonly ownerDependant = computed(() => {
    const dependantId = this.initialValue()?.ownerDependantId;
    return dependantId ? (this.employeeStore.dependantLookupResponses().find((item) => item.id === dependantId) ?? null) : null;
  });

  constructor() {
    this.lookupStore.load([
      LOOKUP_CODES.memberType,
      LOOKUP_CODES.sourceChannel,
      LOOKUP_CODES.requestType,
      LOOKUP_CODES.interventionPriority,
      LOOKUP_CODES.riskRating,
      LOOKUP_CODES.confidentialityLevel,
    ]);
    this.newMemberForm.disable({ emitEvent: false });

    // A new request starts with no member picked; a case that exists shows its own.
    effect(() => {
      const item = this.initialValue();
      const readOnly = this.readOnly();
      untracked(() => (item ? this.populate(item, readOnly) : this.startNew()));
    });

    // Most requests are for an employee, so the user type starts there once the list is loaded.
    effect(() => {
      const employee = this.userTypes().find((item) => item.code === 'EMPLOYEE');
      if (!this.isEdit() && employee && !this.form.controls.userTypeId.value) {
        this.form.controls.userTypeId.setValue(employee.id);
      }
    });

    // The employee a dependant belongs to is the member the card shows.
    effect(() => {
      const employeeId = this.ownerDependant()?.employeeId;
      if (employeeId) {
        untracked(() => this.employeeStore.loadEmployee(employeeId));
      }
    });
  }

  protected selectedEmployee(): EmployeeResponse | null {
    return this.employeeStore.selectedEmployee();
  }

  protected isDependantType(): boolean {
    return this.userTypes().find((item) => item.id === this.form.controls.userTypeId.value)?.code === 'DEPENDENT';
  }

  // Switching between a member who is found and a new member turns the other one's controls off, so only the one in use is checked.
  protected setMode(mode: MemberMode): void {
    this.mode.set(mode);

    if (mode === 'new') {
      this.form.controls.ownerEmployeeId.disable({ emitEvent: false });
      this.newMemberForm.enable({ emitEvent: false });
      this.employeeStore.selectedEmployee.set(null);
    } else {
      this.newMemberForm.disable({ emitEvent: false });
      this.form.controls.ownerEmployeeId.enable({ emitEvent: false });
    }
  }

  protected employeeChanged(employeeId: string): void {
    this.employeeStore.selectedEmployee.set(null);
    if (employeeId) {
      this.employeeStore.loadEmployee(employeeId);
    }
  }

  // The search finds a member by name, employee number, UID, ID number or mobile number.
  protected employeeSearchChanged(searchTerm: string): void {
    this.employeeStore.loadEmployeeLookupResponses(searchTerm);
  }

  protected submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const { newMember, ownerEmployeeId, startDate, targetReviewDate, ...rest } = value;
    const item = this.initialValue();

    if (item) {
      // The member, the owner and the stage stay as they are; only what the request says changes.
      this.updated.emit(
        toUpdateRequest(item, {
          ...cleanRecord(rest),
          interventionTypeId: rest.interventionTypeId ?? item.interventionTypeId,
          startDate: toNullableDateString(startDate) ?? item.startDate,
          targetReviewDate: toNullableDateString(targetReviewDate),
        }),
      );
      return;
    }

    const isNew = this.mode() === 'new';

    this.submitted.emit({
      ...cleanRecord(rest),
      startDate: toNullableDateString(startDate) ?? '',
      targetReviewDate: toNullableDateString(targetReviewDate),
      // The owner type follows from the user type on the server; a dependant is captured in the Eligibility step.
      ownerType: null,
      otherOwnerType: null,
      ownerEmployeeId: isNew ? null : ownerEmployeeId || null,
      ownerDependantId: null,
      currentOwnerPractitionerId: null,
      newMember: isNew ? cleanRecord(newMember) : null,
    } as CreateInterventionRequest);
  }

  // A new request: no member picked yet, and the lists to find or place one.
  private startNew(): void {
    this.clientStore.loadAllClientCompanies();
    this.employeeStore.selectedEmployee.set(null);
    this.employeeStore.loadEmployeeLookupResponses();
  }

  // A case that exists: its request is in the form, and its member is shown, not picked (the member and the user type are fixed once it is logged).
  private populate(item: InterventionResponse, readOnly: boolean): void {
    this.form.patchValue(
      {
        userTypeId: item.userTypeId ?? '',
        interventionTypeId: item.interventionTypeId,
        interventionName: item.interventionName ?? '',
        description: item.description ?? '',
        reason: item.reason ?? '',
        sourceChannelId: item.sourceChannelId ?? '',
        requestTypeId: item.requestTypeId ?? '',
        priorityId: item.priorityId ?? '',
        riskClassificationId: item.riskClassificationId ?? '',
        confidentialityLevelId: item.confidentialityLevelId ?? '',
        startDate: item.startDate,
        targetReviewDate: item.targetReviewDate,
      },
      { emitEvent: false },
    );

    if (readOnly) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
      this.form.controls.ownerEmployeeId.disable({ emitEvent: false });
      this.form.controls.userTypeId.disable({ emitEvent: false });
      this.newMemberForm.disable({ emitEvent: false });
    }

    // The member card is read again only when it is not the member of this case already (a save reads the case again).
    if (item.ownerEmployeeId) {
      if (this.employeeStore.selectedEmployee()?.id !== item.ownerEmployeeId) {
        this.employeeStore.selectedEmployee.set(null);
        this.employeeStore.loadEmployee(item.ownerEmployeeId);
      }
    } else if (item.ownerDependantId) {
      this.employeeStore.selectedEmployee.set(null);
      this.employeeStore.loadDependantLookupResponses();
    }
  }
}
